import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TestTube, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

const RESULTS = [
  { value: "negative", label: "Negative", color: "bg-mystic-700/40 border-white/10 text-mystic-200" },
  { value: "positive", label: "Positive", color: "bg-rose-500/20 border-rose-400/40 text-rose-100" },
  { value: "peak", label: "Peak (LH+)", color: "bg-amber-500/25 border-amber-400/50 text-amber-100" },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

const OPKLoggerCard = () => {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [date, setDate] = useState(todayStr());
  const [result, setResult] = useState("negative");
  const [intensity, setIntensity] = useState(2);
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [recentPositive, setRecentPositive] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/wellness/ttc/opk/history?days=30`, { headers, withCredentials: true });
      setHistory(res.data.entries || []);
      setRecentPositive(res.data.most_recent_positive || null);
    } catch {
      /* silent */
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [token]);

  const log = async () => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/wellness/ttc/opk`,
        { date, result, test_line_intensity: intensity, notes: notes || null },
        { headers, withCredentials: true }
      );
      toast.success(res.data.message || "OPK logged");
      setNotes("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save OPK");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    try {
      await axios.delete(`${API}/wellness/ttc/opk/${d}`, { headers, withCredentials: true });
      toast.success("Entry removed");
      refresh();
    } catch {
      toast.error("Couldn't remove entry");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.24 }}
      data-testid="opk-logger-card"
    >
      <Card className="glass border-amber-400/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <TestTube className="w-5 h-5 text-amber-300" />
            Ovulation Predictor Kit
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {recentPositive && (
            <div
              className="flex items-center gap-2 rounded-xl bg-amber-500/10 border border-amber-400/30 px-3 py-2 text-amber-100 text-sm"
              data-testid="opk-surge-banner"
            >
              <TrendingUp className="w-4 h-4 text-amber-300" />
              LH surge detected on {recentPositive.date} — ovulation likely within 12–36h.
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {RESULTS.map((r) => (
              <button
                key={r.value}
                onClick={() => setResult(r.value)}
                data-testid={`opk-result-${r.value}-btn`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  result === r.value ? r.color : "bg-mystic-800/40 border-white/10 text-mystic-300 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="opk-date-input"
              className="bg-mystic-800/50 border border-mystic-600 rounded-lg px-3 py-2 text-white text-sm focus:border-mystic-400 outline-none"
            />
            <div className="flex items-center gap-2 bg-mystic-800/50 border border-mystic-600 rounded-lg px-3 py-2">
              <span className="text-xs text-mystic-300">Line</span>
              <input
                type="range"
                min="1"
                max="5"
                value={intensity}
                onChange={(e) => setIntensity(parseInt(e.target.value, 10))}
                data-testid="opk-intensity-slider"
                className="flex-1 accent-amber-400"
              />
              <span className="text-xs text-amber-200 w-4">{intensity}</span>
            </div>
          </div>

          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            data-testid="opk-notes-input"
            className="w-full bg-mystic-800/50 border border-mystic-600 rounded-lg px-3 py-2 text-white text-sm focus:border-mystic-400 outline-none resize-none"
            rows={2}
          />

          <Button
            onClick={log}
            disabled={saving}
            data-testid="log-opk-btn"
            className="w-full bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-full"
          >
            {saving ? "Saving..." : "Log OPK"}
          </Button>

          {history.length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <p className="text-xs text-mystic-300 mb-2">Last 30 days · {history.length} entries</p>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {history.slice(0, 8).map((h) => (
                  <li
                    key={h.date}
                    className="flex items-center justify-between text-xs"
                    data-testid={`opk-history-${h.date}`}
                  >
                    <span className="text-mystic-200">{h.date}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
                        h.result === "peak"
                          ? "bg-amber-500/25 text-amber-100"
                          : h.result === "positive"
                          ? "bg-rose-500/20 text-rose-100"
                          : "bg-white/5 text-mystic-300"
                      }`}
                    >
                      {h.result}
                    </span>
                    <button
                      onClick={() => remove(h.date)}
                      className="text-mystic-400 hover:text-rose-300 transition"
                      aria-label={`Delete ${h.date}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default OPKLoggerCard;
